export const PRODUCT_PICKER_MESSAGE_TYPES = {
    selected: 'siteadmin.productPicker.selected',
    requestSelection: 'siteadmin.productPicker.requestSelection',
    selection: 'siteadmin.productPicker.selection',
    selectionChanged: 'siteadmin.productPicker.selectionChanged',
};

const parseJsonArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('[')) {
            try {
                const parsed = JSON.parse(trimmed);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
    }
    return [];
};

export const normalizeProductPayload = (raw) => {
    const id = Number(raw?.id || 0);
    if (!Number.isFinite(id) || id <= 0) {
        return null;
    }

    const title = String(raw?.title || raw?.product_name || '').trim();
    const imageNames = parseJsonArray(raw?.image_names || raw?.imageNames || []);
    const imageUrl = String(raw?.image_url || raw?.imageUrl || raw?.image || imageNames[0] || '').trim();
    const retailPrice = raw?.retail_price ?? raw?.retailPrice ?? null;
    const discountPrice = raw?.discount_price ?? raw?.discountPrice ?? null;
    const quantity = raw?.quantity ?? null;
    const requestedQuantity = raw?.requested_quantity ?? raw?.requestedQuantity ?? 1;
    const unitCost = raw?.unit_cost ?? raw?.unitCost ?? null;
    const customerPrice = raw?.customer_price ?? raw?.customerPrice ?? retailPrice ?? null;
    const supplierCount = raw?.supplier_count ?? raw?.supplierCount ?? 0;

    return {
        id,
        title: title !== '' ? title : null,
        image_url: imageUrl !== '' ? imageUrl : null,
        image_names: imageNames,
        retail_price: Number.isFinite(Number(retailPrice)) ? Number(retailPrice) : null,
        discount_price: Number.isFinite(Number(discountPrice)) ? Number(discountPrice) : null,
        is_ffl: Boolean(raw?.is_ffl ?? raw?.isFfl ?? false),
        is_nfa: Boolean(raw?.is_nfa ?? raw?.isNfa ?? false),
        quantity: Number.isFinite(Number(quantity)) ? Number(quantity) : null,
        requested_quantity: Math.max(1, Math.min(999, Number(requestedQuantity) || 1)),
        unit_cost: unitCost !== null && unitCost !== '' && Number.isFinite(Number(unitCost)) ? Number(unitCost) : null,
        customer_price: customerPrice !== null && customerPrice !== '' && Number.isFinite(Number(customerPrice)) ? Number(customerPrice) : null,
        supplier_count: Math.max(0, Number(supplierCount) || 0),
        available: Boolean(raw?.available ?? false),
    };
};

export const extractPickerPayloadFromElement = (element) => {
    if (!(element instanceof HTMLElement)) {
        return null;
    }

    const rawPayload = element.getAttribute('data-product-picker-payload');
    if (rawPayload) {
        try {
            const parsed = JSON.parse(rawPayload);
            const quantityInput = element.querySelector('[data-product-picker-quantity]');
            if (quantityInput instanceof HTMLInputElement) {
                parsed.requested_quantity = quantityInput.value;
            }
            return normalizeProductPayload(parsed);
        } catch {
            // fall through to DOM-based extraction
        }
    }

    const id = Number(element.getAttribute('data-product-id') || 0);
    if (!Number.isFinite(id) || id <= 0) {
        return null;
    }

    const titleEl = element.querySelector('h3');
    const title = titleEl?.textContent?.trim?.() || '';

    const imgEl = element.querySelector('img');
    const imageUrl = imgEl instanceof HTMLImageElement
        ? (imgEl.currentSrc || imgEl.getAttribute('src') || imgEl.src || '')
        : '';

    return normalizeProductPayload({ id, title, image_url: imageUrl });
};

export const emitSelectedProduct = (
    product,
    { targetWindow = window.parent, targetOrigin = window.location.origin } = {}
) => {
    if (!targetWindow || targetWindow === window) {
        return false;
    }

    const payload = normalizeProductPayload(product);
    if (!payload) {
        return false;
    }

    try {
        targetWindow.postMessage(
            {
                type: PRODUCT_PICKER_MESSAGE_TYPES.selected,
                product: payload,
            },
            targetOrigin
        );
        return true;
    } catch {
        return false;
    }
};

export const emitSelectionResponse = (
    products,
    { targetWindow = window.parent, targetOrigin = window.location.origin } = {}
) => {
    if (!targetWindow || targetWindow === window) {
        return false;
    }

    const normalized = (Array.isArray(products) ? products : [])
        .map((product) => normalizeProductPayload(product))
        .filter(Boolean);

    const productIds = normalized.map((product) => product.id);

    try {
        targetWindow.postMessage(
            {
                type: PRODUCT_PICKER_MESSAGE_TYPES.selection,
                product_ids: productIds,
                products: normalized,
            },
            targetOrigin
        );
        return true;
    } catch {
        return false;
    }
};

export const emitSelectionChanged = (
    products,
    { targetWindow = window.parent, targetOrigin = window.location.origin } = {}
) => {
    if (!targetWindow || targetWindow === window) {
        return false;
    }

    const normalized = (Array.isArray(products) ? products : [])
        .map((product) => normalizeProductPayload(product))
        .filter(Boolean);

    try {
        targetWindow.postMessage(
            {
                type: PRODUCT_PICKER_MESSAGE_TYPES.selectionChanged,
                product_ids: normalized.map((product) => product.id),
                products: normalized,
            },
            targetOrigin
        );
        return true;
    } catch {
        return false;
    }
};

export const requestProductPickerSelection = (
    pickerWindow,
    { clear = false, timeoutMs = 600, targetOrigin = window.location.origin } = {}
) =>
    new Promise((resolve) => {
        if (!pickerWindow) {
            resolve([]);
            return;
        }

        let settled = false;
        const handler = (event) => {
            if (event.origin !== targetOrigin) {
                return;
            }

            const payload = event?.data;
            if (!payload || payload.type !== PRODUCT_PICKER_MESSAGE_TYPES.selection) {
                return;
            }

            settled = true;
            window.removeEventListener('message', handler);

            const products = Array.isArray(payload.products) ? payload.products : [];
            resolve(
                products
                    .map((product) => normalizeProductPayload(product))
                    .filter(Boolean)
            );
        };

        window.addEventListener('message', handler);
        window.setTimeout(() => {
            if (settled) {
                return;
            }
            window.removeEventListener('message', handler);
            resolve([]);
        }, timeoutMs);

        try {
            pickerWindow.postMessage(
                { type: PRODUCT_PICKER_MESSAGE_TYPES.requestSelection, clear },
                targetOrigin
            );
        } catch {
            window.removeEventListener('message', handler);
            resolve([]);
        }
    });
